/**
 * # 校验模块
 * @author nobita
 * @date 2020/5/20
 * ----------
 * @module type
 * @param is* [Any] 检测是否对应属性
 * ----------
 * @module check
 * @param isMobile [String] 手机号
 * @param isEmail [String] 邮箱
 * @param isIdCard [String] 身份证（基础版）
 * @param isDate [String] 有效时间
 * @param isChinese [String] 中文
 * @param isDigit [String] 数字
 * @param isUrl [String] 链接
 * ----------
 * @module has
 * @param hasObject [{ attr, obj }] 检测obj里面是否拥有attr
 * --@return { status: [Boolean], data: obj[attr] || null  }
 * @param hasArray [{ val, arr }] 检测arr里面是否拥有val
 * --@return { status: [Boolean], data: index || null  }
 * ----------
 * @function check 校验方法
 * --@param value [Any] 检测的值
 * --@param rules [String / Array] 检验的规则，多规则的情况下用','分割
 * --@examples check('13888888888', 'isString,isMobile...');
 * --@return 根据rule的校验结果返回
 * @function add 添加校验方法
 * --@param rule [String] 校验规则名称
 * --@param fn [Function] 检验方法
 * --@examples add('isTel', () => {})
 * --@return { status: [Boolean], errMsg: 错误提示信息 }
 */
import isIdCard from './idCard';
const strategy = () => {
  const type = {
    getType (val) {
      const typeStr = Object.prototype.toString.call(val);
      const types = {
        '[object Array]': 'Array',
        '[object Object]': 'Object',
        '[object String]': 'String',
        '[object Function]': 'Function',
        '[object Number]': 'Number',
        '[object Null]': 'Null',
        '[object Undefined]': 'Undefined',
        '[object Boolean]': 'Boolean',
        '[object Set]': 'Set',
        '[object Map]': 'Map',
      }
      return typeStr in types ? types[typeStr] : null
    },
    isArray (val) {
      return this.getType(val) === 'Array';
    },
    isObject (val) {
      return this.getType(val) === 'Object';
    },
    isString (val) {
      return this.getType(val) === 'String';
    },
    isFunction (val) {
      return this.getType(val) === 'Function';
    },
    isNumber (val) {
      return this.getType(val) === 'Number';
    },
    isNull (val) {
      return this.getType(val) === 'Null';
    },
    isUndefined (val) {
      return this.getType(val) === 'Undefined';
    },
    isBoolean (val) {
      return this.getType(val) === 'Boolean';
    },
    isSet (val) {
      return this.getType(val) === 'Set';
    },
    isMap (val) {
      return this.getType(val) === 'Map';
    },
    isEmpty (val) {
      const type = this.getType(val);
      switch (type) {
        case 'String': 
          return val === '';
        case 'Array':
          return Object.keys(val).length === 0;
        case 'Object':
          return Object.keys(val).length === 0;
        case 'Number':
          return Object.is(val, NaN);
        default:
          return !val;
      }
    }
  }
  const check = {
    isMobile (val) {
      // return /^(((13[0-9]{1})|(14[0-9]{1})|(17[0-9]{1})|(15[0-3]{1})|(15[4-9]{1})|(18[0-9]{1})|(199))+\d{8})$/.test(val);
      return  /^1[3-9]\d{9}$/.test(val);
    },
    isIdCard: val => isIdCard(val),
    isDate (val) {
      if ( this.isEmpty(val) ) { return false; }
      return new Date(val).toString() !== 'Invalid Date';
    },
    isChinese (val) {
      return /^[\u4e00-\u9fa5]{0,}$/.test(val);
    },
    isDigit (val) {
      return /^\d+$/.test(val);
    }
  }
  const has = {
    hasObject (data) {
      if ( this.isEmpty(data) ) { return { status: false, data: null } }
      const { attr, obj = {} } = data;
      const b = attr in obj;
      return { status: b, data: b ? obj[attr] : null };
    },
    hasArray (data) {
      if ( this.isEmpty(data) ) { return { status: false, data: null } }
      const {val, arr = []} = data;
      const index = arr.findIndex(v => Object.is(v, val));
      const status = index > -1;
      return { status, data: status ? { index } : null };
    }
  }
  let $strategy = {
    ...type,
    ...check,
    ...has
  }
  return {
    check (value, rules) {
      const rulesType = $strategy.getType(rules);
      let checks;
      switch (rulesType) {
        case 'String':
          checks = rules.split(',');
          break;
        case 'Array':
          checks = rules;
          break;
        default:
          return { value, status: false, msg: '[Rules Error] rules参数有误!' }
      }
      const checkOptions = checks.reduce((obj, v) => {
        obj[v] = v in $strategy ? $strategy[v](value) : undefined;
        return obj;
      }, {});
      return checks.length > 1 ? checkOptions : checkOptions[checks[0]];
    },
    add (rule, fn) {
      if ( rule in $strategy ) {
        console.warn('[Rule Repeat] rule重复!');
        return { status: false, errMsg: '[Rule Repeat] rule重复!' }
      }
      if ( $strategy.isFunction(fn) === false ) {
        console.warn('[Fn Error] fn不是一个函数！');
        return { status: false, errMsg: '[Fn Error] fn不是一个函数！' }
      }
      $strategy[rule] = fn;
      return { status: true }
    }
  }
}
export default new strategy();